import type {Locale} from '@commercial-website/data-layer/locale'
import {locales as defaultLocales} from '@commercial-website/data-layer/locale'
import {LocaleSwitcher as UILocaleSwitcher} from '@commercial-website/ui'
import Cookies from 'js-cookie'
import React from 'react'

import {TrackedLocalizedLink} from '@/components/localized-link'
import {useLocale} from '@/providers/locale'
import {countryFlags} from '@/utils/country-flags'

import {cookiesMap} from '@helios/website/src/config/cookies'
import {useGlobalStrings} from '@helios/website/src/providers/global-strings'
import {usePageInfo} from '@helios/website/src/providers/page-info'

const PLEO_GLOBAL_PAGE_URL = 'https://www.pleo.io/en/global'

interface LocaleSwitcherProps {
    light?: boolean
    locales?: Locale[]
}

type UILocaleSwitcherProps = React.ComponentProps<typeof UILocaleSwitcher>
type LocaleSwitcherValue = UILocaleSwitcherProps['locales'][number]['code']

type GlobalLinkProps = {
    label: string
    children: React.ReactNode
    onClick?: React.MouseEventHandler<HTMLAnchorElement>
}

const GlobalLink = React.forwardRef<HTMLAnchorElement, GlobalLinkProps>(
    ({label, children, ...props}, ref) => {
        return process.env.ENVIRONMENT === 'website' ? (
            <TrackedLocalizedLink
                ref={ref}
                trackingData={{
                    linkText: label,
                    linkType: 'element',
                    elementLocation: 'locale switcher',
                }}
                rawPath
                to={`/en/global`}
                {...props}
            >
                {children}
            </TrackedLocalizedLink>
        ) : (
            <a ref={ref} href={PLEO_GLOBAL_PAGE_URL} {...props}>
                {children}
            </a>
        )
    },
)

const mapLocales = (locales: Locale[], globalLabel: string) => {
    const mappedLocales: UILocaleSwitcherProps['locales'] = locales.map((locale) => ({
        code: locale.code,
        countryName: locale.countryName,
        languageName: locale.languageName,
        flag: countryFlags[locale.country],
        render: ({children, onClick, ...props}) => (
            <TrackedLocalizedLink
                trackingData={{
                    linkText: locale.countryName,
                    linkType: 'element',
                    elementLocation: 'locale switcher',
                }}
                rawPath
                to={`/${locale.urlCode}`}
                {...props}
                onClick={(e) => {
                    e.preventDefault()
                    onClick?.(e)
                }}
            >
                {children}
            </TrackedLocalizedLink>
        ),
    }))
    mappedLocales.push({
        code: 'global',
        label: globalLabel,
        render: ({children, onClick, ...props}) => (
            <GlobalLink label={globalLabel} {...props} onClick={(e) => onClick?.(e)}>
                {children}
            </GlobalLink>
        ),
    })

    return mappedLocales
}

const LocaleSwitcher = ({light, locales: customLocales}: LocaleSwitcherProps) => {
    const currentLocale = useLocale()
    const {pageContext} = usePageInfo()
    const globalStrings = useGlobalStrings()
    const locales = customLocales ?? defaultLocales
    const mappedLocales = mapLocales(locales, globalStrings.localeSelectorGlobalLabel)

    /*
     * Using window.location.assign instead of navigate to force a full page reload.
     * This ensures that the correct experiment cookies are evaluated for the new locale,
     * preventing the user from remaining stuck in a variant specific to their previous locale.
     */
    const handleLocaleSwitch = (localeCode: LocaleSwitcherValue) => {
        const search = window.location.search
        if (localeCode === 'global') {
            if (process.env.ENVIRONMENT === 'website') {
                window.location.assign(`/en/global${search}`)
            } else {
                window.location.assign(`${PLEO_GLOBAL_PAGE_URL}${search}`)
            }
        } else {
            const targetLocale = locales.find((locale) => locale.code === localeCode) ?? locales[0]
            const destination = pageContext.paths?.[targetLocale.code] ?? `/${targetLocale.urlCode}`

            Cookies.set(cookiesMap.defaultCountry, targetLocale.urlCode)

            window.location.assign(`${destination}${search}`)
        }
    }

    return (
        <UILocaleSwitcher
            locales={mappedLocales}
            currentLocaleCode={currentLocale.code}
            onLocaleChange={handleLocaleSwitch}
            compact={!light}
        />
    )
}

export default LocaleSwitcher
